import {
	Box,
	Button,
	Icons,
	Inline,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import { useEffect, useMemo, useRef, useState } from "react"
import config from "../config"
import { getMailMessagePlainText } from "../shared/mailMessagePlainText"

type TMailMessageBodyProps = {
	htmlBody?: string | null
	textBody?: string | null
	quotedHtmlBody?: string | null
	quotedTextBody?: string | null
	messageLabel?: string
	// Upper bound for the auto-grown HTML iframe (px). Tall emails cap here.
	maxHeight?: number
	defaultView?: "text" | "html"
	hideViewToggle?: boolean
	// Controlled rich/plain view. When set, the parent owns the toggle (e.g.
	// via a dropdown) — pair with `hideViewToggle` to hide the built-in buttons.
	view?: "text" | "html"
	onViewChange?: (view: "text" | "html") => void
	// False when the full body was removed from cold storage and could not
	// be re-fetched. The component then shows a notice plus the `preview`
	// snippet instead of an empty body.
	bodyAvailable?: boolean
	// True while a large/deferred body is still being downloaded in the
	// background — the component shows a loading state instead of the
	// "no longer available" notice.
	bodyPending?: boolean
	preview?: string | null
}

// First-party inline (cid) email images: the API serves them from
// `${serverBase}/mailbox/inline-images/{message}/{hash}` behind signed URLs.
// They are message content stored by us — not sender-controlled tracking —
// so they render without the "Show images" opt-in, exactly like `data:`
// images always have.
const INLINE_IMAGE_PATH = "/mailbox/inline-images/"

function trustedInlineImagePrefix(): string | null {
	if (!config.serverBase) return null
	try {
		return new URL(INLINE_IMAGE_PATH, config.serverBase).toString()
	} catch {
		return null
	}
}

function trustedInlineImageOrigin(): string | null {
	if (!config.serverBase) return null
	try {
		return new URL(config.serverBase).origin
	} catch {
		return null
	}
}

function isTrustedInlineImageUrl(value: string | null): boolean {
	const prefix = trustedInlineImagePrefix()
	return !!value && !!prefix && value.trim().startsWith(prefix)
}

// Remote subresources (images, fonts, stylesheets) are how marketing/spam
// mail tracks opens and leaks the reader's IP. Block them by default; the
// user opts in via "Show images". Inline `data:` images, our own signed
// inline-image URLs, and inline styles always render — they carry no
// tracking. `script-src 'none'` + `form-action 'none'` keep the doc inert
// even though the iframe is same-origin (needed for auto-height).
function buildContentSecurityPolicy(allowRemoteContent: boolean): string {
	const trustedOrigin = trustedInlineImageOrigin()
	const img = allowRemoteContent
		? "* data:"
		: `data:${trustedOrigin ? ` ${trustedOrigin}` : ""}`
	const font = allowRemoteContent ? "* data:" : "data:"
	// External stylesheets (e.g. Google Fonts CSS) are a remote fetch too, so
	// only allow them once remote content is unblocked. Inline styles stay on
	// for rendering in both modes.
	const style = allowRemoteContent
		? "'unsafe-inline' https:"
		: "'unsafe-inline'"

	return [
		"default-src 'none'",
		`img-src ${img}`,
		`font-src ${font}`,
		`style-src ${style}`,
		"script-src 'none'",
		"form-action 'none'",
	].join("; ")
}

// Does the HTML reference any remote (http/https) image, stylesheet, font, or
// background — i.e. is there anything to block? Drives the "images hidden"
// bar. Our own inline-image URLs don't count: they always render.
function hasRemoteContent(htmlBody: string): boolean {
	const prefix = trustedInlineImagePrefix()
	const html = prefix
		? htmlBody.split(prefix).join("about:inline-image/")
		: htmlBody

	return (
		/<img\b[^>]*\bsrc\s*=\s*["']?\s*https?:/i.test(html) ||
		/<link\b[^>]*\bhref\s*=\s*["']?\s*https?:/i.test(html) ||
		/@import\s+["']?\s*https?:/i.test(html) ||
		/\burl\(\s*["']?\s*https?:/i.test(html)
	)
}

function isHttpUrl(value: string | null): boolean {
	return !!value && /^\s*https?:/i.test(value)
}

// Replace remote `url(https://…)` in CSS with an empty data: URL so the
// browser makes no request (kills background-image tracking + CSP noise).
// `data:,` is allowed by `img-src data:` and fetches nothing.
function neutralizeRemoteCssUrls(css: string): string {
	return css.replace(
		/url\(\s*(['"]?)\s*https?:[^)]*?\1\s*\)/gi,
		'url("data:,")'
	)
}

/**
 * DOM-sanitize the email before it reaches the iframe.
 * - Always removes <script> (sandbox blocks execution anyway, but it logs a
 *   console error per tag, including inert application/ld+json metadata).
 * - When remote content is blocked, removes the `src`/`srcset` of remote
 *   <img> and drops remote <link rel=stylesheet>, so the browser never makes
 *   the request — no tracking pixel fires, no broken-image icon, no CSP
 *   console noise. The original URL is parked on `data-blocked-src` so the
 *   "Show images" reveal can render with it (we re-sanitize with allow=true).
 *
 * Exported for unit testing.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function sanitizeEmailHtml(
	htmlBody: string,
	allowRemoteContent: boolean
): string {
	const isFullDocument = /<html[\s>]/i.test(htmlBody)
	let doc: Document
	try {
		doc = new DOMParser().parseFromString(htmlBody, "text/html")
	} catch {
		return htmlBody
	}

	doc.querySelectorAll("script").forEach((el) => el.remove())

	// Sandbox already blocks execution, but inline event handlers and
	// javascript: URLs still trigger a "Blocked script execution" console
	// error per attempt (and aren't visible as <script> in inspect). Strip
	// them — defense-in-depth and a clean console.
	const scriptUrl = /^\s*(?:javascript|vbscript|data:text\/html)/i
	const urlAttrs = ["href", "src", "action", "background", "xlink:href"]
	doc.querySelectorAll("*").forEach((el) => {
		el.getAttributeNames().forEach((name) => {
			if (/^on/i.test(name)) {
				el.removeAttribute(name)
			}
		})
		urlAttrs.forEach((attr) => {
			const value = el.getAttribute(attr)
			if (value && scriptUrl.test(value)) {
				el.removeAttribute(attr)
			}
		})
	})

	if (!allowRemoteContent) {
		doc.querySelectorAll("img").forEach((img) => {
			const src = img.getAttribute("src")
			// Our own signed inline-image URLs stay — first-party content.
			if (isHttpUrl(src) && !isTrustedInlineImageUrl(src)) {
				img.setAttribute("data-blocked-src", src as string)
				img.removeAttribute("src")
			}
			const srcset = img.getAttribute("srcset")
			if (srcset) {
				img.setAttribute("data-blocked-srcset", srcset)
				img.removeAttribute("srcset")
			}
		})
		doc.querySelectorAll('link[rel~="stylesheet"][href]').forEach((link) => {
			if (isHttpUrl(link.getAttribute("href"))) {
				link.remove()
			}
		})
		// CSS background images fetch remotely too — in <style> blocks, inline
		// style attributes, and the legacy `background` HTML attribute.
		doc.querySelectorAll("style").forEach((style) => {
			if (style.textContent && /https?:/i.test(style.textContent)) {
				style.textContent = neutralizeRemoteCssUrls(style.textContent)
			}
		})
		doc.querySelectorAll("[style]").forEach((el) => {
			const style = el.getAttribute("style")
			if (style && /url\(/i.test(style)) {
				el.setAttribute("style", neutralizeRemoteCssUrls(style))
			}
		})
		doc.querySelectorAll("[background]").forEach((el) => {
			const background = el.getAttribute("background")
			// `background="cid:…"` rewrites to a signed URL too — keep ours.
			if (isHttpUrl(background) && !isTrustedInlineImageUrl(background)) {
				el.setAttribute("data-blocked-background", background as string)
				el.removeAttribute("background")
			}
		})
	}

	// For a fragment the parser routes head-only elements (a leading <style>,
	// <link>, <meta>) into <head>, so include both head and body or that CSS
	// is lost. A <style> inside our wrapper <body> still applies.
	return isFullDocument
		? `<!doctype html>${doc.documentElement.outerHTML}`
		: doc.head.innerHTML + doc.body.innerHTML
}

function buildIframeDocument(
	htmlBody: string,
	allowRemoteContent: boolean
): string {
	const sanitized = sanitizeEmailHtml(htmlBody, allowRemoteContent)
	const cspMeta = `<meta http-equiv="Content-Security-Policy" content="${buildContentSecurityPolicy(
		allowRemoteContent
	)}" />`

	// A full HTML document (its own <html>/<head>) renders as-is, but still
	// needs our CSP injected — sandbox blocks scripts/forms regardless, this
	// adds the image/font/style gate. Insert into <head>, or synthesize one.
	if (/<html[\s>]/i.test(sanitized)) {
		if (/<head[\s>]/i.test(sanitized)) {
			return sanitized.replace(/<head([^>]*)>/i, `<head$1>${cspMeta}`)
		}

		return sanitized.replace(
			/<html([^>]*)>/i,
			`<html$1><head>${cspMeta}</head>`
		)
	}

	return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  ${cspMeta}
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>
    html, body {
      margin: 0;
      padding: 0;
      background: #ffffff;
    }
    body {
      color: #111827;
      overflow-wrap: anywhere;
    }
    img, video, iframe, table {
      max-width: 100%;
    }
  </style>
</head>
<body>${htmlBody}</body>
</html>`
}

/**
 * Renders email HTML in a locked-down (sandbox, no scripts) iframe and grows
 * the frame to the content height. `allow-same-origin` (without
 * `allow-scripts`) is what lets the parent read the content height; scripts
 * stay disabled, so the email can't run JS — see buildIframeDocument's CSP.
 * NEVER add `allow-scripts` here.
 */
function AutoHeightIframe({
	srcDoc,
	title,
	minHeight = 40,
	maxHeight = 4000,
}: {
	srcDoc: string
	title: string
	minHeight?: number
	maxHeight?: number
}) {
	const ref = useRef<HTMLIFrameElement>(null)
	const [height, setHeight] = useState(minHeight)

	useEffect(() => {
		const iframe = ref.current
		if (!iframe) return
		let observer: ResizeObserver | undefined
		const measure = () => {
			try {
				const doc = iframe.contentDocument
				const body = doc?.body
				if (!doc?.documentElement || !body) return
				// getBoundingClientRect keeps the subpixel height (e.g.
				// 1182.68) that scrollHeight rounds away; ceil it so the frame
				// is never a fraction short — that fraction shows as a 1px
				// scrollbar. Max with documentElement covers body-margin cases.
				const next = Math.max(
					doc.documentElement.scrollHeight,
					Math.ceil(body.getBoundingClientRect().height)
				)
				if (next > 0) {
					setHeight(Math.min(maxHeight, Math.max(minHeight, next)))
				}
			} catch {
				// contentDocument unreadable — leave the fallback height.
			}
		}
		const onLoad = () => {
			measure()
			try {
				const body = iframe.contentDocument?.body
				if (body && "ResizeObserver" in window) {
					observer = new ResizeObserver(() => measure())
					observer.observe(body)
				}
			} catch {
				// ignore — measure() already ran once on load.
			}
		}
		iframe.addEventListener("load", onLoad)
		measure() // srcDoc may already be parsed on mount
		return () => {
			iframe.removeEventListener("load", onLoad)
			observer?.disconnect()
		}
	}, [srcDoc, minHeight, maxHeight])

	return (
		<iframe
			ref={ref}
			title={title}
			srcDoc={srcDoc}
			sandbox="allow-same-origin"
			referrerPolicy="no-referrer"
			style={{
				width: "100%",
				height: `${height}px`,
				border: "0",
				display: "block",
				background: "#fff",
			}}
		/>
	)
}

export function MailMessageBody({
	htmlBody,
	textBody,
	quotedHtmlBody,
	quotedTextBody,
	messageLabel = "email",
	maxHeight = 4000,
	defaultView = "html",
	hideViewToggle = false,
	view,
	onViewChange,
	bodyAvailable = true,
	bodyPending = false,
	preview,
}: TMailMessageBodyProps) {
	const [internalShowHtml, setInternalShowHtml] = useState(
		defaultView === "html"
	)
	// `view`/`onViewChange` let a parent (e.g. the row's actions dropdown) own
	// the rich/plain toggle; uncontrolled otherwise.
	const showHtml = view !== undefined ? view === "html" : internalShowHtml
	const setShowHtml = (next: boolean) => {
		onViewChange?.(next ? "html" : "text")
		if (view === undefined) {
			setInternalShowHtml(next)
		}
	}
	const [showQuotedContent, setShowQuotedContent] = useState(false)
	// Remote images/fonts blocked until the user opts in (open tracking).
	const [showImages, setShowImages] = useState(false)

	const plainText = useMemo(
		() => getMailMessagePlainText({ textBody, htmlBody }),
		[textBody, htmlBody]
	)
	const quotedPlainText = useMemo(
		() =>
			getMailMessagePlainText({
				textBody: quotedTextBody,
				htmlBody: quotedHtmlBody,
			}),
		[quotedTextBody, quotedHtmlBody]
	)
	const normalizedHtml = useMemo(() => htmlBody?.trim() || "", [htmlBody])
	const hasHtmlPreview = normalizedHtml.length > 0
	// Real body content present? `plainText` always carries a "No message
	// content available" fallback, so it can't be used to detect emptiness.
	const hasRealContent = hasHtmlPreview || Boolean(textBody?.trim())
	const normalizedQuotedHtml = useMemo(
		() => quotedHtmlBody?.trim() || "",
		[quotedHtmlBody]
	)
	const hasQuotedHtmlPreview = normalizedQuotedHtml.length > 0
	const hasQuotedContent =
		Boolean(quotedTextBody?.trim()) || Boolean(normalizedQuotedHtml)
	const iframeDocument = useMemo(
		() =>
			hasHtmlPreview ? buildIframeDocument(normalizedHtml, showImages) : "",
		[hasHtmlPreview, normalizedHtml, showImages]
	)
	const quotedIframeDocument = useMemo(
		() =>
			hasQuotedHtmlPreview
				? buildIframeDocument(normalizedQuotedHtml, showImages)
				: "",
		[hasQuotedHtmlPreview, normalizedQuotedHtml, showImages]
	)
	// Only surface the "images hidden" bar when there is something to block.
	const hasBlockableImages = useMemo(
		() =>
			(hasHtmlPreview && hasRemoteContent(normalizedHtml)) ||
			(showQuotedContent &&
				hasQuotedHtmlPreview &&
				hasRemoteContent(normalizedQuotedHtml)),
		[
			hasHtmlPreview,
			normalizedHtml,
			showQuotedContent,
			hasQuotedHtmlPreview,
			normalizedQuotedHtml,
		]
	)

	// Large body deferred at sync and not downloaded yet — show a loading
	// state (the row refetches until it arrives) rather than the body.
	if (bodyPending && !hasRealContent) {
		return (
			<Box borderWidth="1" rounded="md" padding="3" bgColor="subtle">
				<Stack gap="2">
					<Inline gap="2" alignItems="center">
						<Icons.Refresh spin />
						<Text fontSize="sm" color="muted">
							This is a large {messageLabel}. Loading the full message…
						</Text>
					</Inline>
					{preview?.trim() ? (
						<Text whiteSpace="preserveAndWrap">{preview}</Text>
					) : null}
				</Stack>
			</Box>
		)
	}

	// Body was purged from cold storage and could not be re-fetched — there
	// is no full content to render, only the stored snippet.
	if (!bodyAvailable && !hasRealContent) {
		return (
			<Box borderWidth="1" rounded="md" padding="3" bgColor="subtle">
				<Stack gap="2">
					<Inline gap="2" alignItems="center">
						<Icons.Info />
						<Text fontSize="sm" color="muted">
							The original {messageLabel} is no longer available. Showing a
							short preview only.
						</Text>
					</Inline>
					{preview?.trim() ? (
						<Text whiteSpace="preserveAndWrap">{preview}</Text>
					) : null}
				</Stack>
			</Box>
		)
	}

	return (
		<Stack gap="2" position="relative">
			{hideViewToggle || !hasHtmlPreview ? null : (
				<Inline
					gap="2"
					alignItems="center"
					flexWrap="wrap"
					position="absolute"
					top="0"
					right="0"
					padding="2"
				>
					<Button
						size="sm"
						level={!showHtml ? "secondary" : "tertiary"}
						onClick={() => setShowHtml(false)}
						title="View as Plain Text"
					>
						<Icons.CodeXml />
					</Button>
					<Button
						size="sm"
						level={!showHtml ? "tertiary" : "secondary"}
						onClick={() => setShowHtml(true)}
						title="View as Rich/HTML Text"
					>
						<Icons.Eye />
					</Button>
				</Inline>
			)}
			{showHtml && hasBlockableImages && !showImages ? (
				<Inline
					gap="2"
					alignItems="center"
					flexWrap="wrap"
					padding="2"
					rounded="md"
					bgColor="subtle"
					borderWidth="1"
				>
					<Icons.Info />
					<Text fontSize="sm" color="muted">
						Images in this {messageLabel} are hidden to protect your privacy.
					</Text>
					<Button
						size="sm"
						level="tertiary"
						onClick={() => setShowImages(true)}
					>
						Show images
					</Button>
				</Inline>
			) : null}
			{showHtml && hasHtmlPreview ? (
				<AutoHeightIframe
					title={`${messageLabel} HTML preview`}
					srcDoc={iframeDocument}
					maxHeight={maxHeight}
				/>
			) : null}
			{!showHtml || !hasHtmlPreview ? (
				<Box overflow="hidden">
					<Text whiteSpace="preserveAndWrap">{plainText}</Text>
				</Box>
			) : null}
			{hasQuotedContent ? (
				<Inline gap="2" alignItems="center" flexWrap="wrap">
					<Button
						size="sm"
						level={showQuotedContent ? "primary" : "tertiary"}
						onClick={() => setShowQuotedContent((current) => !current)}
						title={showQuotedContent ? "Hide quoted text" : "Show quoted text"}
					>
						<Icons.DotsVertical rotate="90" />
					</Button>
				</Inline>
			) : null}
			{showQuotedContent && hasQuotedContent ? (
				showHtml && hasQuotedHtmlPreview ? (
					<AutoHeightIframe
						title={`${messageLabel} quoted HTML preview`}
						srcDoc={quotedIframeDocument}
						maxHeight={maxHeight}
					/>
				) : (
					<Text whiteSpace="preserveAndWrap">{quotedPlainText}</Text>
				)
			) : null}
		</Stack>
	)
}
